<template>
  <content-subpage
    :visible="visible"
    :titles="titles"
    class="announcement-edit-content-subpage school-notification-edit-content-subpage"
    @content-subpage-hidden="actionHidden"
  >
    <template v-if="showHeaderButton" #header-buttons>
      <fetch-data-button
        :kind="dataSaveButton.kind"
        :size="dataSaveButton.size"
        :error="dataSaveButton.error"
        :loading="dataSaveButton.loading"
        :icon-only="dataSaveButton.iconOnly"
        :tip-position="dataSaveButton.tipPosition"
        :error-message="dataSaveButton.errorMessage"
        :completed="dataSaveButton.completed"
        @click="handleUpdate"
        @reset-error-state="handleSaveButtonResetError"
        @reset-completed-state="handleSaveButtonResetCompleted"
      >
        <svg-icon :name="dataSaveButton.iconName" kind="action" />
      </fetch-data-button>
    </template>
    <template #main-content>
      <div class="announcement-editor" :class="{ edit: isEdit }">
        <overlay-loader v-if="isLoading" />
        <template v-else>
          <div v-if="isEdit" class="announcement-actions">
            <overflow-menu :flip-menu="true">
              <template #trigger>
                <svg-icon name="Kebab24" kind="action" />
              </template>
              <OverflowMenuItem @click="handleDelete">
                <svg-icon name="Pencil24" kind="action" />
                {{ t("button_delete") }}
              </OverflowMenuItem>
            </overflow-menu>
          </div>
          <div class="announcement-editor-content">
            <cv-form class="announcement">
              <div class="field-group sender-receiver">
                <!-- Edit Mode -->
                <template v-if="isEdit">
                  <dl class="field-set type-text receiver">
                    <dt class="field-label">
                      {{ t("announcement_receiver_receiver_selection_label") }}
                    </dt>
                    <dd class="field-value">{{ notificationReceiver }}</dd>
                  </dl>
                  <dl v-if="hasIndividualReceiver" class="field-set type-text individual">
                    <dt class="field-label">
                      {{ t("announcement_individual_receiver_form_label") }}
                    </dt>
                    <dd class="field-value">{{ notificationIndividualReceiver }}</dd>
                  </dl>
                  <dl class="field-set owner">
                    <dt class="field-label">
                      {{ t("announcement_owner_form_label") }}
                    </dt>
                    <dd class="field-value">
                      {{ notification.createdByName }}
                    </dd>
                  </dl>
                  <dl v-if="isCommunity" class="field-set type-text class-group">
                    <dt class="field-label">
                      {{ t("announcement_receiver_community_selection_label") }}
                    </dt>
                    <dd class="field-value">
                      <label-component
                        kind="class-group"
                        :text="community.name"
                        :subject-key="community.subjectKey"
                      />
                    </dd>
                  </dl>
                  <dl class="field-set type-text school">
                    <dt class="field-label">{{ t("announcement_receiver_school_form_label") }}</dt>
                    <dd class="field-value">{{ schoolName }}</dd>
                  </dl>
                  <dl class="field-set last-modified-by">
                    <dt class="field-label">
                      {{ t("announcement_last_modified_by_form_label") }}
                    </dt>
                    <dd class="field-value">
                      {{ notification.modifiedByName }}
                    </dd>
                  </dl>
                </template>
                <!-- Create Mode -->
                <template v-else>
                  <dl class="field-set type-dropdown receiver">
                    <dt class="field-label">{{ t("announcement_receiver_school_form_label") }}</dt>
                    <dd class="field-input">
                      <dropdown-component
                        kind="white"
                        :value="schoolSelected"
                        @change="handleSchoolChange"
                      >
                        <cv-dropdown-item
                          v-for="option in manageableSchoolOptions"
                          :key="option.value"
                          :value="option.value"
                        >
                          {{ option.name }}
                        </cv-dropdown-item>
                      </dropdown-component>
                    </dd>
                  </dl>
                  <dl class="field-set type-text recipient">
                    <dt class="field-label">
                      {{ t("announcement_receiver_receiver_selection_label") }}
                    </dt>
                    <dd class="field-input">
                      <dropdown-component
                        kind="white"
                        :value="audienceTypeSelected"
                        @change="handleAudienceTypeChange"
                      >
                        <cv-dropdown-item
                          v-for="(type, index) in audienceTypes"
                          :key="index"
                          :value="type"
                        >
                          {{ getAudienceTypeLabel(type) }}
                        </cv-dropdown-item>
                      </dropdown-component>
                    </dd>
                  </dl>
                  <dl class="field-set type-tag form-class">
                    <dd class="field-input">
                      <multi-select
                        kind="white"
                        class="form-class-filter"
                        :label="formClassFilterLabel"
                        :disabled="isFormClassFilterDisabled"
                        selection-feedback="fixed"
                        :options="formClassCodesOptions"
                        :value="formClassSelected"
                        @change="handleFormClassChange"
                      >
                        <template v-if="error.formClass !== ''" #invalid-message>
                          {{ error.formClass }}
                        </template>
                      </multi-select>
                    </dd>
                  </dl>
                  <dl class="field-set type-text individual-teacher">
                    <dd class="field-input add-individual-teacher">
                      <cv-button
                        :disabled="isAddIndividualTeachersDisabled"
                        size="field"
                        kind="tertiary"
                        @click="handleAddTeachers"
                      >
                        <svg-icon name="Plus24" kind="action" />
                        {{ t("announcement_receiver_add_teachers_label") }}
                      </cv-button>
                    </dd>
                    <dd class="field-value individual-recipients">
                      <ul>
                        <li v-for="user in individualTeachers" :key="user.userId">
                          <user-tag
                            :user="user"
                            :show-detail-on-hover="true"
                            :show-detail-on-click="true"
                            :removable="true"
                            @remove="handleRemoveTeacher"
                          >
                            <template #label>
                              <label-component kind="recipient" :text="user.username" />
                            </template>
                          </user-tag>
                        </li>
                      </ul>
                    </dd>
                  </dl>
                </template>
              </div>
              <dl class="field-set title">
                <dt class="field-label">{{ t("announcement_title_form_label") }}</dt>
                <dd class="field-input">
                  <text-input
                    :value="formData.title"
                    :mandatory="true"
                    placeholder="Title"
                    :character-limit="titleCharacterLimit"
                    :error-message="error.title"
                    @input="handleTitleChange"
                  />
                </dd>
              </dl>
              <dl class="field-set message">
                <dt class="field-label">{{ t("message_form_label") }}</dt>
                <dd class="field-input">
                  <rich-text-editor
                    ref="richTextEditor"
                    kind="teacher_tinymce_4"
                    :initial-content="formData.content"
                    size="large"
                    :is-mandatory="true"
                    @change="handleMessageChange"
                  />
                </dd>
              </dl>
              <div class="field-group schedule">
                <!-- Start Date Time -->
                <dl class="field-set start-date">
                  <dd class="field-input">
                    <DatePicker
                      class="date-picker"
                      kind="single"
                      :date-label="t('announcement_start_date_form_label')"
                      :value="formData.startDate"
                      :cal-options="calendarOption"
                      :invalid-message="error.startDate"
                      placeholder="dd Mmm yyyy"
                      @change="onStartDateChange"
                    />
                    <text-input
                      class="time-picker"
                      :label="t('announcement_start_time_form_label')"
                      :input-type="'time'"
                      :value="formData.startTime"
                      :error-message="error.startTime"
                      @change="onUpdateStartTime"
                    />
                  </dd>
                </dl>
              </div>
            </cv-form>
          </div>
        </template>
      </div>
    </template>
    <template v-if="canSubmit" #action-button>
      <fetch-data-button
        :kind="dataSaveButton.kind"
        :error="dataSaveButton.error"
        :loading="dataSaveButton.loading"
        :error-message="dataSaveButton.errorMessage"
        tip-position="top"
        tip-alignment="end"
        @click="handleSend"
        @reset-error-state="handleSaveButtonResetError"
        @reset-completed-state="handleSaveButtonResetCompleted"
      >
        <template v-if="dataSaveButton.completed">
          {{ t("button_state_saved") }}
        </template>
        <template v-else-if="dataSaveButton.error">
          {{ t("button_state_error") }}
        </template>
        <template v-else>
          <svg-icon name="Plus24" kind="action" />
          {{ t("button_create") }}
        </template>
      </fetch-data-button>
    </template>
  </content-subpage>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from "vue";
import { useI18n } from "vue-i18n";
import { useVm } from "@utils/vm";
import { useErrorHandler } from "@plugins/common/composables/error.ts";
import {
  ContentSubpageParams,
  useContentSubpage,
} from "@plugins/content-subpage/composables/content-subpage.ts";
import { useContentSubpageInstance } from "@plugins/content-subpage/composables/content-subpage-instance.ts";
import {
  NOTIFICATION_AUDIENCE,
  NOTIFICATION_SCOPE,
  SCHOOL_NOTIFICATION_AUDIENCE_OPTIONS,
  SCHOOL_NOTIFICATION_AUDIENCE_OPTIONS_LABEL,
} from "@modules/notification/service/notification.constant";
import SchoolApi from "@apis/school.api";
import NotificationEditContentSubpageMixin from "@modules/notification/mixin/notification.edit.content.subpage.mixin";
import { LOCKABLE_RESOURCE_TYPE } from "@plugins/common/service/lock.constant";
import LockService from "@plugins/common/service/lock.service";
import NotificationUtils from "@modules/notification/service/notification.utils";
import TeacherSelectionListingContentSubpage from "@plugins/teacher-selection-listing/components/TeacherSelectionListingContentSubpage.vue";
import { isDefined, isEmpty } from "@/utils/var.check";
import UserTag from "@plugins/user-tag/components/UserTag.vue";
import userApi from "@apis/user.api";
import SchoolOptionsMixins from "@plugins/school/mixins/school.options.mixins";

// Props
interface SchoolNotificationEditContentSubpageParam extends ContentSubpageParams {
  kind?: string;
  data?: Record<string, unknown>;
}
interface Props {
  params?: SchoolNotificationEditContentSubpageParam | null;
}
const props = withDefaults(defineProps<Props>(), {
  params: null,
});

// defineOptions
defineOptions({
  mixins: [NotificationEditContentSubpageMixin, SchoolOptionsMixins],
});

// Composables
const { t } = useI18n();
const { vm, vmExpose } = useVm();
const { passiveApiFailHandler } = useErrorHandler();

const id = props.params.id;
const dirtyId = ref(id);

const {
  titles,
  visible,
  dataSaveButton,
  handleSaveButtonResetError,
  handleSaveButtonResetCompleted,
  actionHidden,
  defaultExpose,
  setDataState,
  setErrorMessage,
  setDirty,
  removeDirty,
} = useContentSubpage(props.params);

const contentSubpageInstance = useContentSubpageInstance();

// Data
const schools = ref<unknown[]>([]);
const schoolSelected = ref("");
const audienceTypes = ref(SCHOOL_NOTIFICATION_AUDIENCE_OPTIONS);
const audienceTypeSelected = ref("");
const formClassCodes = ref<unknown[]>([]);
const formClassSelected = ref<unknown[]>([]);
const individualTeachers = ref<unknown[]>([]);

// Computed
const isFormClassFilterDisabled = computed(
  () => audienceTypeSelected.value !== SCHOOL_NOTIFICATION_AUDIENCE_OPTIONS.FORM_CLASS,
);
const isCommunity = computed(() => vm.params?.data?.classGroup?.publicDomain ?? false);
const community = computed(() => {
  if (vm.params?.data?.classGroup) {
    return {
      name: vm.params.data.classGroup.groupName,
      subjectKey: vm.params.data.classGroup.subjectKey,
    };
  }
  return null; // Or undefined
});
const formClassCodesOptions = computed(() => {
  if (!formClassCodes.value.length) return [];
  return formClassCodes.value.reduce((result, classCode) => {
    if (classCode !== "STAFF") {
      result.push({
        name: classCode,
        label: classCode,
        value: classCode,
      });
    }
    return result;
  }, []);
});
const formClassFilterLabel = computed(() => t("announcement_form_class_filter_label"));
const isAddIndividualTeachersDisabled = computed(
  () =>
    audienceTypeSelected.value === NOTIFICATION_AUDIENCE.ALL ||
    audienceTypeSelected.value === NOTIFICATION_AUDIENCE.ALL_TEACHERS,
);
const notificationReceiver = computed(() =>
  NotificationUtils.getNotificationGroupReceiver(vm.notification),
); // Via vm
const notificationIndividualReceiver = computed(() =>
  NotificationUtils.getNotificationIndividualReceiver(vm.notification),
);
const hasIndividualReceiver = computed(() => !isEmpty(notificationIndividualReceiver.value));
const schoolName = computed(() => vm.notification.notificationRecipients[0].schoolName); // Via vm

// Methods
function validate() {
  let isValid = vm.validateBasicFields();
  // Validate form class
  if (
    !vm.isEdit &&
    audienceTypeSelected.value === NOTIFICATION_AUDIENCE.FORM_CLASS &&
    vm.formData.formClassCodes.length === 0
  ) {
    vm.error.formClass = t("announcement_form_class_empty_message");
  }
  for (const key in vm.error) {
    if (vm.error[key] !== "") {
      isValid = false;
    }
  }
  return isValid;
}

function getAudienceTypeLabel(type: string) {
  if (type === SCHOOL_NOTIFICATION_AUDIENCE_OPTIONS.FORM_CLASS) {
    const school = getSchoolById(schoolSelected.value);
    return `${t(SCHOOL_NOTIFICATION_AUDIENCE_OPTIONS_LABEL[type])} (${school.academicYear})`; // Show form class label with year
  }
  return t(SCHOOL_NOTIFICATION_AUDIENCE_OPTIONS_LABEL[type]);
}

function getRequestData() {
  const requestData: Record<string, unknown> = {
    title: vm.formData.title,
    rt_content: vm.formData.content,
    startDateTime: vm.formData.startDateTime,
    notify: vm.formData.notify,
    scope: vm.scope, // Via vm
  };
  if (vm.isEdit) {
    // Via vm
    requestData.uuid = vm.notification.uuid;
    const audienceTypes: string[] = [];
    const individualIds: string[] = [];
    const formClassCodesLocal: string[] = [];
    vm.notification.notificationRecipients.forEach(
      (recipient: { audienceType?: string; formClassCode?: string; userId?: string }) => {
        if (
          recipient.audienceType &&
          recipient.audienceType !== NOTIFICATION_AUDIENCE.INDIVIDUAL &&
          recipient.audienceType !== NOTIFICATION_AUDIENCE.FORM_CLASS
        ) {
          audienceTypes.push(recipient.audienceType);
        }
        if (
          recipient.audienceType &&
          recipient.audienceType === NOTIFICATION_AUDIENCE.INDIVIDUAL &&
          !audienceTypes.includes(NOTIFICATION_AUDIENCE.INDIVIDUAL)
        ) {
          audienceTypes.push(recipient.audienceType);
        }
        if (
          recipient.audienceType &&
          recipient.audienceType === NOTIFICATION_AUDIENCE.FORM_CLASS &&
          !audienceTypes.includes(NOTIFICATION_AUDIENCE.FORM_CLASS)
        ) {
          audienceTypes.push(recipient.audienceType);
        }
        if (recipient.formClassCode) {
          formClassCodesLocal.push(recipient.formClassCode);
        }
        if (recipient.userId) {
          individualIds.push(recipient.userId);
        }
      },
    );
    requestData.audienceTypes = audienceTypes;
    requestData.formClassCodes = formClassCodesLocal;
    requestData.individualIds = individualIds;
    // All recipients should have same year and school
    requestData.academicYear = vm.notification.notificationRecipients[0].academicYear;
    requestData.schoolId = vm.notification.notificationRecipients[0].schoolId;
  } else {
    const school = getSchoolById(schoolSelected.value);
    requestData.academicYear = school.academicYear;
    requestData.schoolId = vm.formData.schoolId;

    const audienceTypesLocal = [audienceTypeSelected.value];
    if (
      (audienceTypeSelected.value === NOTIFICATION_AUDIENCE.FORM_CLASS ||
        audienceTypeSelected.value === NOTIFICATION_AUDIENCE.ALL_STUDENTS) &&
      !isEmpty(vm.formData.individualIds)
    ) {
      // ALL_STUDENTS + INDIVIDUAL / FORM_CLASS + INDIVIDUAL
      audienceTypesLocal.push(NOTIFICATION_AUDIENCE.INDIVIDUAL);
    }

    requestData.audienceTypes = audienceTypesLocal;
    requestData.formClassCodes = vm.formData.formClassCodes;
    requestData.individualIds = vm.formData.individualIds;
  }
  return requestData;
}

function populateForm() {
  vm.populateCommonFields(); // Via vm
  if (!vm.isEdit) {
    vm.formData.schoolId = parseInt(schoolSelected.value);
    vm.formData.formClassCodes = [];
  }
}

function getSchoolById(schoolId: string) {
  return schools.value.find((school) => school.id === parseInt(schoolId));
}

function handleSchoolChange(schoolId: string) {
  if (schoolSelected.value === schoolId) return;
  setDirty(dirtyId.value);
  const prevSelectedSchool = schoolSelected.value;
  schoolSelected.value = schoolId;
  if (!isEmpty(vm.formData.formClassCodes) || !isEmpty(vm.formData.individualIds)) {
    if (!confirm(t("dirty_confirmation_prompt"))) {
      schoolSelected.value = prevSelectedSchool; // No $nextTick needed
      return;
    } else {
      formClassSelected.value = [];
      vm.formData.formClassCodes = [];
      individualTeachers.value = [];
      vm.formData.individualIds = [];
    }
  }
  audienceTypeSelected.value = SCHOOL_NOTIFICATION_AUDIENCE_OPTIONS.ALL;
  vm.formData.schoolId = schoolId;
}
function handleAudienceTypeChange(audienceType: string) {
  if (audienceTypeSelected.value === audienceType) return;
  setDirty(dirtyId.value);
  const prevAudienceType = audienceTypeSelected.value;
  audienceTypeSelected.value = audienceType;
  if (!promptIfFormClassAndTeachersDirty(prevAudienceType, audienceType)) return;
  vm.error.formClass = "";
  if (audienceType === NOTIFICATION_AUDIENCE.FORM_CLASS) {
    SchoolApi.fetchSchoolAdminFormClasses(schoolSelected.value).then((response) => {
      if (response.code === "error") {
        return;
      }
      if (response.data) {
        formClassCodes.value = response.data;
      }
    }, passiveApiFailHandler);
  }
}

function handleFormClassChange(formClassCodesLocal: unknown[]) {
  setDirty(dirtyId.value);
  formClassSelected.value = formClassCodesLocal;
  vm.formData.formClassCodes = formClassCodesLocal;
  vm.error.formClass = "";
}

function handleAddTeachers(e: Event) {
  e.preventDefault();
  if (
    audienceTypeSelected.value === NOTIFICATION_AUDIENCE.ALL ||
    audienceTypeSelected.value === NOTIFICATION_AUDIENCE.ALL_TEACHERS
  )
    return;
  const school = getSchoolById(schoolSelected.value);
  contentSubpageInstance.show(
    TeacherSelectionListingContentSubpage,
    {
      baseTitle: [...titles.value], // From useContentSubpage
      hasParentSubpage: true,
      excludedUserIds: vm.formData.individualIds,
      selectedSchoolCode: school.schoolCode,
      disableFilter: true,
      actionHidden: vm.params?.actionHidden, // From vm
      fetchTeachers: userApi.fetchTeachers,
      fetchSchools: SchoolApi.getAdministratedSchools,
    },
    {
      onTeacherSelected: onTeacherSelected,
    },
  );
}

function onTeacherSelected(selectedUsers) {
  if (!isEmpty(selectedUsers)) {
    setDirty(dirtyId.value);
    addSelectedTeachersInList(selectedUsers);
  }
}

function addSelectedTeachersInList(selectedUsers: { id: string; name: string; email: string }[]) {
  individualTeachers.value.sort((a, b) => a.indexNo - b.indexNo); // Assumption: indexNo on teachers
  selectedUsers.forEach((user) => {
    individualTeachers.value.unshift({
      userId: user.id,
      username: user.name,
      email: user.email,
      index: individualTeachers.value.length === 0 ? 0 : individualTeachers.value[0].index - 1,
    });
    if (vm.formData.individualIds) {
      if (!vm.formData.individualIds.includes(user.id)) {
        vm.formData.individualIds.push(user.id);
      }
    } else {
      vm.formData.individualIds = [user.id];
    }
  });
}
function handleRemoveTeacher(userId) {
  setDirty(dirtyId.value);
  individualTeachers.value = individualTeachers.value.filter(
    (teacher) => teacher.userId !== userId,
  );
  const index = vm.formData.individualIds.indexOf(userId);
  vm.formData.individualIds.splice(index, 1);
}

function promptIfFormClassAndTeachersDirty(prevAudienceType: string, audienceType = "") {
  const isFormClassSelected = !isEmpty(vm.formData.formClassCodes);
  const isTeachersSelected = !isEmpty(vm.formData.individualIds);
  const isAllAndAllTeacherAudience =
    audienceType === NOTIFICATION_AUDIENCE.ALL ||
    audienceType === NOTIFICATION_AUDIENCE.ALL_TEACHERS;
  const isAllStudentsAudience = audienceType === NOTIFICATION_AUDIENCE.ALL_STUDENTS;

  if (isAllAndAllTeacherAudience && (isFormClassSelected || isTeachersSelected)) {
    if (!confirm(t("dirty_confirmation_prompt"))) {
      audienceTypeSelected.value = prevAudienceType; // No $nextTick needed
      return false;
    } else {
      resetFormClassAndTeachers();
      return true;
    }
  }
  if (isAllStudentsAudience && isFormClassSelected) {
    if (!confirm(t("dirty_confirmation_prompt"))) {
      audienceTypeSelected.value = prevAudienceType;
      return false;
    } else {
      formClassSelected.value = [];
      vm.formData.formClassCodes = [];
      return true;
    }
  }
  // No dirty, proceed
  return true;
}

function resetFormClassAndTeachers() {
  formClassSelected.value = [];
  vm.formData.formClassCodes = [];
  individualTeachers.value = [];
  vm.formData.individualIds = [];
}

// Lifecycle
onMounted(() => {
  if (vm.isEdit) {
    // Via vm
    vm.lockService = new LockService(
      LOCKABLE_RESOURCE_TYPE.NOTIFICATION,
      vm.notification.uuid,
      vm.handleLockRetrievalFail,
    );
    vm.lockService.startLockInterval().then(
      () => {
        populateForm();
        vm.checkAndUpdateStartDateValue();
        vm.isLoading = false;
      },
      () => {
        /* Empty Function */
      },
    );
  } else {
    schools.value = vm.params?.data?.schoolData ?? [];
    schoolSelected.value = vm.defaultSelectedSchool; // SchoolOptionsMixins via vm
    audienceTypeSelected.value = NOTIFICATION_AUDIENCE.ALL;
    populateForm();
    vm.isLoading = false;
  }

  if (isDefined(vm.scope) && vm.scope === NOTIFICATION_SCOPE.SCHOOL && schools.value) {
    schools.value = schools.value.filter((school) => school.manageable);
  }
});

onBeforeUnmount(() => {
  if (vm.lockService) {
    vm.lockService.destroy();
    delete vm.lockService;
  }
});

defineExpose(defaultExpose);

vmExpose({
  id,
  actionHidden,
  setDirty,
  removeDirty,
  setDataState,
  setErrorMessage,
  validate,
  getRequestData,
  schools,
});
</script>
